import { Home } from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import WatchIcon from '@mui/icons-material/Watch';
import {
  Box,
  Divider,
  Drawer as NormalDrawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { styled, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import React, { useMemo, useState } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import useProfile from '../shared/use-profile';
import AccountButton from './AccountButton';

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: open ? 'flex-end' : 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const Pages = [
  {
    name: 'Home',
    icon: <Home />,
    path: '/home',
  },
  {
    name: 'My Devices',
    icon: <WatchIcon />,
    path: '/devices',
  },
];

function MyDrawer() {
  const profile = useProfile();
  const pages = useMemo(() => {
    const permissions = profile.role.permissions;
    return Pages.filter((page) => {
      if (!page.permissions) {
        return true;
      }
      let allowed = false;
      page.permissions.forEach((perm) => {
        if (permissions.includes(perm)) {
          allowed = true;
        }
      });
      return allowed;
    });
  }, [profile]);
  return (
    <div>
      <Divider />
      <List>
        {pages.map((page, index) => (
          <ListItem
            key={page.path}
            disablePadding
            component={Link}
            to={page.path}
            style={{ color: 'inherit' }}
          >
            <ListItemButton>
              <ListItemIcon>{page.icon}</ListItemIcon>
              <ListItemText primary={page.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );
}

function MainPageTemplate({ children, title }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Box component="nav">
        <NormalDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          <MyDrawer />
        </NormalDrawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
          }}
          open={open}
        >
          <DrawerHeader theme={theme} open={open}>
            {open ? (
              <IconButton onClick={handleDrawerOpen}>
                <ChevronLeftIcon />
              </IconButton>
            ) : (
              <IconButton onClick={handleDrawerOpen}>
                <MenuIcon />
              </IconButton>
            )}
          </DrawerHeader>
          <MyDrawer />
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{
              marginRight: { xs: 2, sm: 0 },
              ...(open && { display: 'none' }),
              display: { xs: 'block', sm: 'none' },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Theora Link Pro™
          </Typography>
          <AccountButton />
        </Toolbar>
        <Box sx={{ pl: 3, pr: 3, pb: 5 }}>{children}</Box>
      </Box>
      <style jsx>{`
        .logo {
          filter: drop-shadow(0px 4px 1px #aaaaaa);
        }
      `}</style>
    </Box>
  );
}

export default MainPageTemplate;
